<script setup>
import {computed} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import UserProfilePic from '@/components/users/UserProfilePic.vue';
import {useNavigationItems} from '@/composables/useNavigationItems';
import {useAuthService} from '@/composables/useAuthService';

const router = useRouter();
const route = useRoute();

const {userTopRightItems, onUserTopRightItemClick} = useNavigationItems();
const {isAuthenticated, user} = useAuthService();
</script>
<template >
  <v-menu right offset-y v-if="isAuthenticated">
    <template v-slot:activator="{ props }">
      <v-list class="tl-user-list">
        <!-- X215 (Grant 2026-10-28: "a horrible rounded edge on hover"): the trigger is a 48px box with the bar icon
             buttons' 12px corners, centred in the 64px bar (the list has no block padding), so its hover tint reads like
             theirs. X163 made it a pill-cornered 64px item: a bar-high stadium. rounded="lg" still keeps Vuetify's
             rounded-0 (!important) off the item; the scoped rule below sets the 12px -->
        <v-list-item height="48" rounded="lg" class="ma-0 tl-user-item" v-bind="props">
          <!-- X152 (R73): the avatar and its chevron stay on one line (the item's content box is not a flex row, so at 390 the
               chevron dropped under the avatar); below 600px the chevron goes, the avatar alone is the menu -->
          <span class="tl-user-trigger">
            <user-profile-pic :image-clickable="false" size="medium" :user="user"></user-profile-pic>
            <v-icon color="textSubtle" class="tl-user-chev d-none d-sm-inline-flex">mdi-chevron-down</v-icon>
          </span>
        </v-list-item>
      </v-list>
    </template>
    <v-list density="compact">
      <v-list-item :to="{name : 'UserDashboard'}" exact>
        <v-list-item-title>
          <v-icon size="16">mdi-view-dashboard</v-icon>
          Admin dashboard
        </v-list-item-title>
      </v-list-item>
      <v-list-item exact v-for="(item, i) in userTopRightItems" :key="i" @click="onUserTopRightItemClick(item)">
        <v-list-item-title>
          <v-icon size="16" :icon="item.icon"></v-icon>
          {{ item.text }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>


<style scoped lang="scss">
.user-dropdown {
  font-size: 12px;
}

/* X152 (R73): one row, never wrapping; the chevron sits beside the avatar */
.tl-user-trigger {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.tl-user-chev {
  flex: 0 0 auto;
}

/* X215: the activator list adds no 8px block padding (that pushed a 64px item to the bar's edges) and no fill */
.tl-user-list {
  padding: 0 !important;
  background: transparent !important;
}

/* from 600px the box sits 8px off the bar's right edge, as the icon buttons sit 8px apart (mr-2); a phone keeps the
   room for the club card (X182 / X201) */
@media (min-width: 600px) {
  .tl-user-list {
    margin-right: 8px;
  }
}

/* X215: the bar icon buttons' shape (.tl-iconbtn: 12px corners), never a pill. 4px inline padding (Vuetify's 16px
   one-line padding-inline is (0,3,0), so !important) keeps the 40px avatar and its chevron snug in the box. The hover
   and focus overlay inherit this radius */
.tl-user-item {
  border-radius: 12px !important;
  padding-inline: 4px !important;
}

/* X154 (R74 note) / X163 (R75 L1): the keyboard focus the trigger keeps after Esc closes the menu is a ring that
   follows its 12px corners, never a square grey box. The ring sits inside the item: the toolbar content and the list
   both clip overflow, so a ring drawn outside the box was never seen */
.tl-user-item:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -3px;
}
</style>
